import { useCallback } from 'react';
import { useLocation } from '@reach/router';

import { steps, translateRoute, useTranslatedNavigate } from '../utils/routes';
import { DossierItemCategory } from '../models/DossierItemModel';
import { RequirementType } from '../views/DirectFeedbackUpload';
import { jsonApiAxios } from '../services/api';
import routes from '../messages/routes';
import { ClaimModel } from '../models/ClaimModel';
import useAuthenticate from './useAuthenticate';
import { useBuild } from '../services/GlobalStore/GlobalStore';
import { PageType } from '../views/Passengers/PassengerContainer';

interface DocumentsNavigateParams {
  claimId?: string | null;
  documentType?:
    | RequirementType.Authorization
    | RequirementType.BookingDocs
    | RequirementType.BookingConfirmation;
  passengerId?: string | null;
  pageType: PageType;
}

export type DocumentPathResolver = (direction?: 1 | -1) => Promise<string>;

type RequirementsOrder = RequirementType[];

const requirementOrderMap: {
  booking_confirmation: number;
  booking_docs: number;
  identification: number;
} = {
  ['identification']: 0,
  ['booking_docs']: 1,
  ['booking_confirmation']: 2,
};

/**
 * Documentation:
 * https://greenclaim.atlassian.net/wiki/spaces/GCA4/pages/2290745345/Upload+document+flow
 * @param claimId
 * @param documentType
 * @param passengerId
 */
export const useDocumentsNavigate = ({
  claimId,
  documentType,
  passengerId,
  pageType,
}: DocumentsNavigateParams) => {
  const { token, isLoggedIn } = useAuthenticate();
  const { state, pathname } = useLocation();
  const disableBackButton = state?.disableBackButton ?? false;
  const translatedNavigate = useTranslatedNavigate();

  const claim: ClaimModel = useBuild('claims', claimId);

  /**
   * Based on requirements it returns the next path to redirect
   * This function uses early return so the order of checks matter
   */
  const determineRedirectPath: DocumentPathResolver = useCallback(
    async (direction = 1): Promise<string> => {
      const data = await jsonApiAxios.get(
        passengerId
          ? `/passengers/${passengerId}/missingDossierItemCategoriesRequirements`
          : `/claims/${claim.id}/missingDossierItemCategoriesRequirements`,
        token
          ? {
              headers: { Authorization: `Bearer ${token}` },
            }
          : undefined
      );

      /**
       * Building array of requirements to find what's the order and if
       * requirements exists. It will simply return array of requirements.
       * That's why we use fixed array indexes. If there is no requirement
       * array element will be empty - so indicating no requirement is needed.
       */
      const requiredDocumentsOrder = data?.reduce(
        (acc: RequirementsOrder, requirement: DossierItemCategory) => {
          if (requirement.slug === 'customer_docs_identification')
            acc[0] = routes[steps.UploadId];
          if (
            ['customer_docs_boarding_pass', 'customer_docs_e_ticket'].includes(
              requirement.slug
            )
          )
            acc[1] = routes[steps.UploadBoardingPass];
          if (requirement.slug === 'customer_docs_booking_confirmation')
            acc[2] = routes[steps.UploadBookingConfirmation];
          if (
            (requirement.slug === 'customer_docs_extra_costs' &&
              pageType === PageType.Claim) ||
            (requirement.slug === 'customer_docs_extra_costs' &&
              pageType === PageType.Funnel)
          )
            acc[3] = routes[steps.UploadAdditionalCostAlternativeFlight];
          return acc;
        },
        []
      );

      /**
       * Now we are looking for next not empty element in array.
       * We know what is current requirement from a map tha represents
       * requirement and its index. Look: requirementOrderMap[documentType].
       *
       * For going forward we just need to find next not empty element.
       * Below code just creates new array from all requirements from the point
       * of next requirement relatively to current.
       * Then to omit not required we filter array from falsy elements.
       *
       */
      const newRoute =
        direction > 0
          ? requiredDocumentsOrder
              // Remove current requirement so we have new array
              .slice(requirementOrderMap?.[documentType] + 1)
              // Remove falsy elements so we omit route when no requirement
              .filter(Boolean)[0] // and we select first element if going forwards
          : requiredDocumentsOrder
              .slice(0)
              // Remove current requirement starting from end
              .slice(0, requirementOrderMap?.[documentType])
              // Remove falsy elements so we omit route when no requirement
              .filter(Boolean)
              // Reverse as we want last element of unknown length
              .reverse()[0]; // and we select last element when going backwards

      /**
       * If no next document found then means we go out from documents upload
       * loop
       */
      if (!newRoute) {
        /**
         * Forward direction
         */
        if (direction === 1) {
          // On shareable passenger page we redirect to thank you
          if (
            (passengerId && pageType === PageType.Passenger) ||
            claim?.product_type === 'french_court_delays'
          )
            return routes[steps.ThankYou];
          if (claim?.product_type === 'flight_compensation')
            return routes[steps.BookingReference];
          return routes[steps.ClaimAmount];
        } else if (direction === -1) {
          /**
           * Backwards
           */
          return routes[steps.PassengersList];
        }
      }
      return newRoute;
    },
    [claim, documentType, passengerId, token, pageType]
  );

  async function navigateNext(): Promise<void> {
    const basepath =
      PageType.Funnel === pageType
        ? translateRoute(routes[steps.CongratulationsBasepath]) +
          `/${claim.claim_number}`
        : PageType.Passenger === pageType
        ? translateRoute(routes[steps.Passenger]) + `/${passengerId}`
        : PageType.Claim === pageType
        ? translateRoute(routes[steps.Claim]) + `/${claim.claim_number}`
        : '';

    return translatedNavigate(
      basepath !== ''
        ? '/' +
            translateRoute(await determineRedirectPath(), {
              basepath: `${basepath}`,
            })
        : translateRoute([
            pathname.replace(/\/[\w-]+$/, ''),
            await determineRedirectPath(),
          ]),
      token || isLoggedIn
        ? {
            state: {
              disableBackButton,
            },
          }
        : undefined
    );
  }

  async function navigateBack(): Promise<void> {
    const newRoute = translateRoute([
      pathname.replace(/\/[\w-]+$/, ''),
      await determineRedirectPath(-1),
    ]);
    return translatedNavigate(
      newRoute,
      token || isLoggedIn
        ? {
            state: {
              disableBackButton,
            },
          }
        : undefined
    );
  }

  return { navigateNext, navigateBack, determineRedirectPath };
};
